import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  GoogleMap,
  Autocomplete,
  DirectionsService,
  DirectionsRenderer,
  useJsApiLoader
} from '@react-google-maps/api';
import './AddTripModal.css'; // Reuse same CSS

import { useGetAllAssetsQuery, useGetAllLiteDevicesQuery, useGetAllUsersQuery } from '../../redux/admin/adminApi';
import { useGetClientsQuery, useGetMaterialsQuery } from '../../redux/logistics/logisticsApi';
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_VERSION } from '../../config/googleMaps';
import Loader from '../../mainframes/Loader';

const libraries = ['places', 'geometry', 'drawing','visualization'];

const mapContainerStyle = {
  width: '100%',
  height: '350px',
  borderRadius: '8px',
  marginTop: '10px'
};

const defaultCenter = { lat: 20, lng: 0 };
const normalizeRole = (value) => String(value || "").trim().toLowerCase();

const normalizeCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.assets)) return payload.assets;
  if (Array.isArray(payload.users)) return payload.users;
  if (Array.isArray(payload.clients)) return payload.clients;
  if (Array.isArray(payload.materials)) return payload.materials;
  return [];
};

const extractId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value?._id || value?.id || value?.$oid || '';
};

const extractMaterialValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value?._id || value?.name || value?.materialName || '';
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const tzOffset = parsed.getTimezoneOffset() * 60000;
  return new Date(parsed.getTime() - tzOffset).toISOString().slice(0, 16);
};

const isTripDriverOption = (user) => {
  const role = normalizeRole(user?.role);
  return role === "driver";
};

const extractDirectionsPath = (directions) => {
  const route = directions?.routes?.[0];
  const overviewPath = Array.isArray(route?.overview_path) ? route.overview_path : [];

  if (overviewPath.length >= 2) {
    return overviewPath
      .map((point) => ({
        lat:
          typeof point?.lat === "function"
            ? point.lat()
            : Number(point?.lat),
        lng:
          typeof point?.lng === "function"
            ? point.lng()
            : Number(point?.lng),
      }))
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  }

  return [];
};

const EditTripModal = ({ onClose, onSubmit, tripData }) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const { data: usersResponse = [], isLoading: isUsersLoading } = useGetAllUsersQuery();
  const { data: assetResponse, isLoading: isVehicleLoading } = useGetAllAssetsQuery();
  const { data: devicesResponse = [] } = useGetAllLiteDevicesQuery();
  const { data: materialsResponse = [], isLoading: isMaterialsLoading } = useGetMaterialsQuery();

const { data: clientsData = [], isLoading: isClientsLoading } = useGetClientsQuery();
const clients = normalizeCollection(clientsData);
  const users = normalizeCollection(usersResponse);
  const drivers = Array.isArray(users) ? users.filter((u) => !u?.isDeleted).filter(isTripDriverOption) : [];
  const materials = normalizeCollection(materialsResponse);
  const assets = normalizeCollection(assetResponse);
  const devices = normalizeCollection(devicesResponse);
  const vehicles = useMemo(() => {
    const normalizedAssets = assets.map((v) => ({
      _id: v._id,
      IMEI: v.IMEI || v.imei || '',
      model: v.model || '',
      name: v.name || '',
    }));
    const normalizedDevices = devices.map((v) => ({
      _id: v._id,
      IMEI: v.imei || v.IMEI || '',
      model: v.model || '',
      name: v.name || '',
    }));
    const merged = normalizedAssets.length ? normalizedAssets : normalizedDevices;
    const selectedVehicleId = extractId(tripData?.vehicle);
    const fallbackSelected =
      !merged.some((item) => String(item._id) === String(selectedVehicleId)) && selectedVehicleId
        ? [{
            _id: selectedVehicleId,
            IMEI: tripData?.vehicle?.IMEI || tripData?.vehicle?.imei || '',
            model: tripData?.vehicle?.model || '',
            name: tripData?.vehicle?.name || 'Selected Vehicle',
          }]
        : [];
    return [...fallbackSelected, ...merged];
  }, [assets, devices, tripData]);

  const [formData, setFormData] = useState({
    client: extractId(tripData?.client),
    vehicle: extractId(tripData?.vehicle),
    driver: extractId(tripData?.driver),
    material: extractMaterialValue(tripData?.material),
    load: tripData?.load || '',
    startAddr: tripData?.startAddr || '',
    endAddr: tripData?.endAddr || '',
    startDate: toDateInputValue(tripData?.startDate),
    endDate: toDateInputValue(tripData?.endDate),
    routeName: tripData?.routeName || '',
    distance: tripData?.distance || '',
    duration: tripData?.duration || ''
  });

  const [errors, setErrors] = useState({});
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [map, setMap] = useState(null);

  const originRef = useRef(null);
  const destRef = useRef(null);

  useEffect(() => {
    setFormData({
      client: extractId(tripData?.client),
      vehicle: extractId(tripData?.vehicle),
      driver: extractId(tripData?.driver),
      material: extractMaterialValue(tripData?.material),
      load: tripData?.load || '',
      startAddr: tripData?.startAddr || '',
      endAddr: tripData?.endAddr || '',
      startDate: toDateInputValue(tripData?.startDate),
      endDate: toDateInputValue(tripData?.endDate),
      routeName: tripData?.routeName || '',
      distance: tripData?.distance || '',
      duration: tripData?.duration || '',
    });
    setDirectionsResponse(null);
  }, [tripData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const directionsCallback = useCallback((response) => {
    if (response && response.status === 'OK') {
      setDirectionsResponse(response);

      const route = response.routes[0].legs[0];

      setFormData(prev => ({
        ...prev,
        distance: route.distance.text,
        duration: route.duration.text,
        startAddr: route.start_address,
        endAddr: route.end_address
      }));
    }
  }, []);

  const onPlaceSelected = () => {
    setDirectionsResponse(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.client) newErrors.client = 'Client is required';
    if (!formData.vehicle) newErrors.vehicle = 'Vehicle is required';
    if (!formData.driver) newErrors.driver = 'Driver is required';
    if (!formData.routeName) newErrors.routeName = 'Route name is required';
    if (!formData.startAddr) newErrors.startAddr = 'Start address is required';
    if (!formData.endAddr) newErrors.endAddr = 'End address is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      alert("Please fill all required fields");
      return;
    }

    const originLoc = originRef.current?.getPlace?.()?.geometry?.location;
    const destLoc = destRef.current?.getPlace?.()?.geometry?.location;
    const nextRoutePath = extractDirectionsPath(directionsResponse);

    onSubmit(tripData._id, {
      ...formData,
      startPoint:
        originLoc && typeof originLoc.lat === "function" && typeof originLoc.lng === "function"
          ? { lat: originLoc.lat(), lng: originLoc.lng() }
          : tripData?.startPoint,
      endPoint:
        destLoc && typeof destLoc.lat === "function" && typeof destLoc.lng === "function"
          ? { lat: destLoc.lat(), lng: destLoc.lng() }
          : tripData?.endPoint,
      routePath: nextRoutePath.length
        ? nextRoutePath
        : Array.isArray(tripData?.routePath)
          ? tripData.routePath
          : [],
      selectedPathSummary: String(
        directionsResponse?.routes?.[0]?.summary || tripData?.selectedPathSummary || ""
      ).trim(),
    });
  };

  if (!isLoaded || isUsersLoading) {
    return <Loader label="Loading Map..." minHeight="100vh" background="rgba(15, 23, 42, 0.45)" />;
  }

  return (
    <div className="atm-modal-overlay">
      <div className="atm-modal-content" style={{ maxWidth: '1200px', maxHeight: '90vh', overflowY: 'auto' }}>

        <div className="atm-modal-header">
          <h3>Edit Trip</h3>
          <button className="atm-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="atm-modal-body">

          {/* ROW 1: CLIENT, VEHICLE, DRIVER */}
          <div className="atm-form-grid-3">

            <div className="atm-form-group">
              <label>Client Name *</label>
              <select
                name="client"
                className={`atm-form-control ${errors.client ? 'atm-error' : ''}`}
                value={formData.client}
                onChange={handleChange}
              >
                <option value="">-- Select Client --</option>
                {isClientsLoading ? (
                  <option disabled>Loading clients...</option>
                ) : clients.length === 0 ? (
                  <option disabled>No clients available</option>
                ) : (
                  clients.map(c => (
                    <option key={c._id} value={c._id}>
                      {c.clientName || c.username || c.name} ({c.contactPersonPhone || c.mobileNo || c.email || "No contact"})
                    </option>
                  ))
                )}
              </select>
              {errors.client && <span className="atm-error-text">{errors.client}</span>}
            </div>

            <div className="atm-form-group">
              <label>Vehicle *</label>
              <select
                name="vehicle"
                className={`atm-form-control ${errors.vehicle ? 'atm-error' : ''}`}
                value={formData.vehicle}
                onChange={handleChange}
              >
                <option value="">-- Select Vehicle --</option>
                {isVehicleLoading ? (
                  <option disabled>Loading vehicles...</option>
                ) : vehicles.length === 0 ? (
                  <option disabled>No vehicles available</option>
                ) : (
                  vehicles.map(v => (
                      <option key={v._id} value={v._id}>
                        {v.model || v.name || 'Vehicle'} ({v.IMEI})
                      </option>
                    ))
                )}
              </select>
              {errors.vehicle && <span className="atm-error-text">{errors.vehicle}</span>}
            </div>

            <div className="atm-form-group">
              <label>Driver *</label>
              <select
                name="driver"
                className={`atm-form-control ${errors.driver ? 'atm-error' : ''}`}
                value={formData.driver}
                onChange={handleChange}
              >
                <option value="">-- Select Driver --</option>
                {isUsersLoading ? (
                  <option disabled>Loading drivers...</option>
                ) : drivers.length === 0 ? (
                  <option disabled>No drivers available</option>
                ) : (
                  drivers.map(d => (
                    <option key={d._id} value={d._id}>
                    {d.username || d.name} ({d.mobileNo || d.email || d.uniqueId || "No contact"})
                    </option>
                  ))
                )}
              </select>
              {errors.driver && <span className="atm-error-text">{errors.driver}</span>}
            </div>

          </div>

          {/* ROW 2: MATERIAL, LOAD, DISTANCE */}
          <div className="atm-form-grid-3">
            <div className="atm-form-group">
              <label>Material</label>
              <select
                name="material"
                className="atm-form-control"
                value={formData.material}
                onChange={handleChange}
              >
                <option value="">-- Select Material --</option>
                {materials.map(m => (
                  <option key={m._id || m.name} value={m._id || m.name}>
                    {m.name} ({m.category})
                  </option>
                ))}
              </select>
            </div>

            <div className="atm-form-group">
              <label>Load (tons)</label>
              <input 
                type="number" 
                name="load" 
                className="atm-form-control" 
                value={formData.load}
                onChange={handleChange}
                placeholder="e.g., 5"
                min="0"
                step="0.1"
              />
            </div>

            <div className="atm-form-group">
              <label>Route Length / Duration</label>
              <input
                type="text"
                className="atm-form-control atm-readonly"
                readOnly
                value={formData.distance ? `${formData.distance} (${formData.duration})` : ''}
                placeholder="Auto-filled after route"
              />
            </div>
          </div>

          {/* ROW 3: DATES */}
          <div className="atm-form-grid-2">
            <div className="atm-form-group">
              <label>Start Date & Time *</label>
              <input
                type="datetime-local"
                name="startDate"
                className={`atm-form-control ${errors.startDate ? 'atm-error' : ''}`}
                value={formData.startDate}
                onChange={handleChange}
              />
              {errors.startDate && <span className="atm-error-text">{errors.startDate}</span>}
            </div>

            <div className="atm-form-group">
              <label>End Date & Time (Optional)</label>
              <input
                type="datetime-local"
                name="endDate"
                className="atm-form-control"
                value={formData.endDate}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* ROUTE NAME */}
          <div className="atm-form-group">
            <label>Route Name *</label>
            <input
              type="text"
              name="routeName"
              className={`atm-form-control ${errors.routeName ? 'atm-error' : ''}`}
              value={formData.routeName}
              onChange={handleChange}
              placeholder="e.g., Delhi to Mumbai via NH48"
            />
            {errors.routeName && <span className="atm-error-text">{errors.routeName}</span>}
          </div>

          {/* ADDRESSES */}
          <div className="atm-form-grid-2">
            <div className="atm-form-group">
              <label>Start Address *</label>
              <Autocomplete onLoad={ref => originRef.current = ref} onPlaceChanged={onPlaceSelected}>
                <input
                  type="text"
                  name="startAddr"
                  className={`atm-form-control ${errors.startAddr ? 'atm-error' : ''}`}
                  value={formData.startAddr}
                  onChange={handleChange}
                  placeholder="Enter start location"
                />
              </Autocomplete>
              {errors.startAddr && <span className="atm-error-text">{errors.startAddr}</span>}
            </div>

            <div className="atm-form-group">
              <label>End Address *</label>
              <Autocomplete onLoad={ref => destRef.current = ref} onPlaceChanged={onPlaceSelected}>
                <input
                  type="text"
                  name="endAddr"
                  className={`atm-form-control ${errors.endAddr ? 'atm-error' : ''}`}
                  value={formData.endAddr}
                  onChange={handleChange}
                  placeholder="Enter destination"
                />
              </Autocomplete>
              {errors.endAddr && <span className="atm-error-text">{errors.endAddr}</span>}
            </div>
          </div>

          {/* MAP */}
          <div className="atm-map-wrapper">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={2}
              onLoad={(mapInstance) => setMap(mapInstance)}
            >
              {formData.startAddr && formData.endAddr && (
                  <DirectionsService
                    options={{
                      origin: formData.startAddr,
                      destination: formData.endAddr,
                      travelMode: 'DRIVING'
                    }}
                    callback={directionsCallback}
                  />
                )}

              {directionsResponse && (
                <DirectionsRenderer directions={directionsResponse} />
              )}
            </GoogleMap>
          </div>

        </div>

        {/* FOOTER */}
        <div className="atm-modal-footer">
          <button className="atm-btn-submit" onClick={handleSubmit}>
            UPDATE TRIP
          </button>
          <button className="atm-btn-cancel" onClick={onClose}>
            CANCEL
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditTripModal;
